import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { TextureView } from '~/engine/view/TextureView';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { MOUSE_BUTTONS_LEFT_MASK } from '~/engine/tool/MouseButton';
import { ContourOperations } from '~/engine/util/algorithms/sdf/ContourOperations';
import { LineInProgress } from '~/engine/core/render/overlayCanvas';
import { DrawActionState, DrawingConstraints } from '../DrawActionState';
import { LinePreviewRenderer } from './LinePreviewRenderer';

export class Line {
    /** The maximum distance in pixels between the first point and cursor
     *  to snap the line closed when clicking point-by-point. */
    private static readonly CLICK_SNAP_TO_CLOSE_TOLERANCE_PX = 15;
    /** The maximum distance in pixels between the first point and cursor
     * to snap the line closed when drag-drawing. */
    private static readonly DRAG_SNAP_TO_CLOSE_TOLERANCE_PX = 10;

    private _pointsMm: vec3[];
    private _hoverPointMm: vec3 | null;
    private readonly _epsilonToAddPointMm = 0.1;
    private minLineLength = 10;
    private _undoMarkers: number[] = [];

    constructor(startPoint: vec3) {
        this._pointsMm = [startPoint];
        this._hoverPointMm = null;
    }

    public get pointsMm(): readonly vec3[] {
        return this._pointsMm;
    }

    public get hoverPointMm(): vec3 | null {
        return this._hoverPointMm;
    }

    /** Get the maximum distance in pixels between the first point and cursor
     *  to snap the line closed when clicking point-by-point. */
    public static get clickSnapToCloseTolerancePx(): number {
        return Line.CLICK_SNAP_TO_CLOSE_TOLERANCE_PX;
    }

    /** Get the maximum distance in pixels between the first point and cursor
     *  to snap the line closed when drag-drawing. */
    public static get dragSnapToCloseTolerancePx(): number {
        return Line.DRAG_SNAP_TO_CLOSE_TOLERANCE_PX;
    }

    public hover(ptMm: vec3 | null) {
        this._hoverPointMm = ptMm;
    }

    public isClosingPoint(point: vec3, closeEpsilonMm: number): boolean {
        if (!this._hoverPointMm || this._pointsMm.length < 3) {
            return false;
        }

        const pointsClose = this.arePointsClose(
            point,
            this._pointsMm[0],
            closeEpsilonMm
        );

        const lineLength = this.calculateTotalLength();
        const lengthSufficient = lineLength >= this.minLineLength;

        return pointsClose && lengthSufficient;
    }

    /** Record the current point count so that undoLastSegment
     *  can revert back to this position. Call before addPoint
     *  at the start of each new user action (mouse press). */
    public pushUndoMarker(): void {
        this._undoMarkers.push(this._pointsMm.length);
    }

    /** Remove all points added since the last undo marker.
     *  Skips stale markers whose position matches the current
     *  length (e.g. if addPoint rejected the point). Returns
     *  false when no markers remain (only the starting point
     *  is left). */
    public undoLastSegment(): boolean {
        while (this._undoMarkers.length > 0) {
            const marker = this._undoMarkers.pop()!;
            if (marker < this._pointsMm.length) {
                this._pointsMm.length = marker;
                return true;
            }
        }
        return false;
    }

    public addPoint(point: vec3, closeEpsilonMm: number): boolean {
        const lineReady = this.isClosingPoint(point, closeEpsilonMm);
        if (lineReady) {
            this._hoverPointMm = null;
            return true;
        }

        if (this._pointsMm.length > 0) {
            const lastPoint = this._pointsMm[this._pointsMm.length - 1];
            if (
                this.arePointsClose(point, lastPoint, this._epsilonToAddPointMm)
            ) {
                return false;
            }
        }
        this._pointsMm.push(point);
        this._hoverPointMm = null;
        return false;
    }

    private arePointsClose(
        point1: vec3,
        point2: vec3,
        epsilonMm: number
    ): boolean {
        return vec3.distance(point1, point2) < epsilonMm;
    }

    private calculateTotalLength(): number {
        let length = 0;
        for (let i = 1; i < this._pointsMm.length; i++) {
            length += vec3.distance(this._pointsMm[i - 1], this._pointsMm[i]);
        }
        return length;
    }
}

/**
 * A line drawn on one plane view by clicks and drags, from its first point
 * until its owner closes or drops it. A click or a drag release near the first
 * point closes the line, unless Shift is held.
 */
export class LineDrawGesture {
    public readonly view: PlaneView;
    public readonly line: Line;
    private readonly onShiftToggled: () => void;

    /** True after a press, so that the drag following it becomes an undo
     *  step of its own, apart from the straight segment the press added. */
    private needsDragMarker = true;
    private _isMouseHeld = true;
    private isShiftHeld: boolean;

    private readonly handleKeyEvent = (e: KeyboardEvent) => {
        if (e.shiftKey !== this.isShiftHeld) {
            this.isShiftHeld = e.shiftKey;
            this.onShiftToggled();
        }
    };

    /**
     * Starts a line at a pressed point.
     * @param onShiftToggled Called when Shift is pressed or released, which
     * can change what {@link preview} returns.
     */
    public constructor(
        view: PlaneView,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        onShiftToggled: () => void
    ) {
        this.view = view;
        this.line = new Line(view.clientToPatientCoordinates(coordinates));
        this.isShiftHeld = modifierKeys.hasShift;
        this.onShiftToggled = onShiftToggled;

        // Add individual listeners here outside of ToolManager to bypass
        // its multiple places where event propagation is stopped. This way
        // we get modifier keys (shift) here
        document.addEventListener('keydown', this.handleKeyEvent);
        document.addEventListener('keyup', this.handleKeyEvent);
    }

    public dispose(): void {
        document.removeEventListener('keydown', this.handleKeyEvent);
        document.removeEventListener('keyup', this.handleKeyEvent);
    }

    /** True from a press until the release that follows it. */
    public get isMouseHeld(): boolean {
        return this._isMouseHeld;
    }

    /** The line as it should be painted now. */
    public preview(): LineInProgress {
        const hoverPointMm = this.line.hoverPointMm;
        const tolerancePx = this._isMouseHeld
            ? Line.dragSnapToCloseTolerancePx
            : Line.clickSnapToCloseTolerancePx;
        return {
            pointsMm: this.line.pointsMm,
            hoverPointMm,
            closing:
                hoverPointMm !== null &&
                !this.isShiftHeld &&
                this.line.isClosingPoint(
                    hoverPointMm,
                    this.closeEpsilonMm(tolerancePx)
                ),
        };
    }

    /**
     * Ends a straight segment at the pressed point.
     * @returns True when the press closed the line instead of adding a point.
     */
    public press(
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2
    ): boolean {
        this._isMouseHeld = true;
        this.line.pushUndoMarker();
        this.needsDragMarker = true;
        return this.line.addPoint(
            this.view.clientToPatientCoordinates(coordinates),
            modifierKeys.hasShift
                ? 0
                : this.closeEpsilonMm(Line.clickSnapToCloseTolerancePx)
        );
    }

    /** Moves the hover point, and extends the line to it while the left button is held. */
    public move(coordinates: ReadonlyVec2, buttons: number): void {
        const point = this.view.clientToPatientCoordinates(coordinates);
        this.line.hover(point);
        if (buttons === MOUSE_BUTTONS_LEFT_MASK) {
            if (this.needsDragMarker) {
                this.line.pushUndoMarker();
                this.needsDragMarker = false;
            }
            this.line.addPoint(point, 0);
            this.line.hover(point);
        }
    }

    /**
     * Ends a press or a drag.
     * @returns True when the release closed the line.
     */
    public release(coordinates: ReadonlyVec2): boolean {
        this._isMouseHeld = false;
        if (this.line.pointsMm.length < 3) {
            return false;
        }
        const point = this.view.clientToPatientCoordinates(coordinates);
        this.line.hover(point);
        return this.line.isClosingPoint(
            point,
            this.isShiftHeld
                ? 0
                : this.closeEpsilonMm(Line.dragSnapToCloseTolerancePx)
        );
    }

    /** Ends a press or a drag without moving the line or testing whether it closes. */
    public endHold(): void {
        this._isMouseHeld = false;
    }

    private closeEpsilonMm(tolerancePx: number): number {
        const res = this.view.resolution;
        return tolerancePx * Math.max(res[0], res[1]);
    }
}

export class LineDrawActionState extends DrawActionState {
    private gesture: LineDrawGesture | null = null;
    protected readonly _planeView: PlaneView;
    private readonly renderer: LinePreviewRenderer;

    private readonly renderPreview = () => {
        this.renderer.render(this.gesture?.preview() ?? null);
    };

    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        targetImage: ImageObject,
        isErasing: boolean,
        constraints?: DrawingConstraints
    ) {
        super(view, target, targetImage, isErasing, constraints);
        this._planeView = view;

        this.renderer = new LinePreviewRenderer(view, target);
    }

    public dispose(): void {
        this.gesture?.dispose();
        this.renderer.dispose();
    }

    public get drawing(): boolean {
        return this.gesture !== null;
    }

    /** True while the left button is held on a line in progress. */
    public get mouseHeld(): boolean {
        return this.gesture?.isMouseHeld === true;
    }

    public get actionName(): string {
        return `Line ${this._erase ? 'erase' : 'draw'}`;
    }

    protected get contourTexture(): ContourTexture | undefined {
        if (!this.gesture) {
            return undefined;
        }
        return new ContourOperations().polygonToSdf(
            this.gesture.line.pointsMm,
            this.targetImage
        );
    }

    // NOTE: live preview clipping is intentionally NOT implemented for the line
    // tool. Its in-progress preview is a vector polyline ({@link LinePreviewRenderer}),
    // not a filled SDF region — there is no fill to intersect/subtract until the
    // polygon is closed. Drawing-constraints are therefore applied only at
    // commit (inherited {@link DrawActionState.applyBoolean} →
    // applyDrawingConstraints), which is the first point the stroke exists as an
    // SDF. Clipping the polyline path itself would be a separate 2D-polygon
    // operation; deferred until there is a need.

    public onMousePress(
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        if (!this.gesture) {
            this.gesture = new LineDrawGesture(
                this._planeView,
                modifierKeys,
                coordinates,
                this.renderPreview
            );
        } else if (this.gesture.press(modifierKeys, coordinates)) {
            return this.finishDrawing();
        }
        this.renderPreview();
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        if (this.gesture) {
            this.gesture.move(coordinates, buttons);
            this.renderPreview();
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        coordinates: ReadonlyVec2,
        _view?: TextureView
    ): EventHandlerResult {
        if (this.gesture?.release(coordinates)) {
            return this.finishDrawing();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /** Ends a press or a drag without moving the line or closing it. */
    public endHold(): void {
        this.gesture?.endHold();
    }

    public finishOnPoint(coordinates: ReadonlyVec2): EventHandlerResult {
        if (this.gesture) {
            this.gesture.line.addPoint(
                this._planeView.clientToPatientCoordinates(coordinates),
                0
            );
            return this.finishDrawing();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    private finishDrawing(): EventHandlerResult {
        this.applyBoolean();
        this.discardDrawing();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            {
                layers: LayerBit.CONTOUR_LAYER_BIT,
                volume: this._targetStructure.boundingBox,
            }
        );
    }

    public clearMemory(): void {
        this.renderer.clear();
    }

    public undoLastPoint(): boolean {
        if (!this.gesture?.line.undoLastSegment()) {
            return false;
        }
        this.renderPreview();
        return true;
    }

    public discardDrawing(): void {
        this.renderer.clear();
        this.gesture?.dispose();
        this.gesture = null;
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
